import React from 'react';
import { useIntl } from 'react-intl';
import {
  Autocomplete,
  Box,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CenteredFlexBox, ColumnFlexBox } from '../../FlexBox';
import IconSelect from '../../Inputs/IconSelect';
import useTranslate from '../../../Hooks/useTranslate';
import PredicateField from './PredicateField';
import DependsOnField from './DependsOnField';

/**
 * How wide a control stands: a switch takes what it needs, the ones holding a list of their own take
 * a row to themselves, and everything else shares the row.
 */
const CONTROL_WIDTH = {
  switch: 'auto',
  colour: 'auto',
  predicate: '1 1 100%',
  dependsOn: '1 1 100%',
};

/**
 * Picks a key of the section, for a setting that names one rather than holding a value.
 *
 * @param {Object} props - Component props.
 * @param {String} props.label - What the setting is called.
 * @param {String} props.value - The key chosen.
 * @param {Array} props.catalogue - The keys the section can show.
 * @param {Function} props.onChange - Receives the chosen key, empty for none.
 *
 * @returns {React.JSX.Element}
 */
export const KeyField = function KeyField({
  label,
  value = '',
  catalogue = [],
  onChange = () => {},
}) {
  const translate = useTranslate();

  const chosen =
    catalogue.find((known) => known.key === value) ??
    (value ? { key: value, label: value } : null);

  // A key saved before the catalogue knew it still has to be offered, or the field shows as invalid.
  const options =
    chosen && !catalogue.some((known) => known.key === chosen.key)
      ? [chosen, ...catalogue]
      : catalogue;

  return (
    <Autocomplete
      size="small"
      options={options}
      value={chosen}
      onChange={(_, option) => onChange(option?.key ?? '')}
      getOptionLabel={(option) => translate(option?.label ?? option?.key)}
      isOptionEqualToValue={(option, held) => option.key === held.key}
      renderOption={(props, option) => (
        <li {...props} key={option.key}>
          <ColumnFlexBox>
            <Typography variant="body2">
              {translate(option.label ?? option.key)}
            </Typography>
            <Typography variant="caption" sx={{ opacity: '60%' }}>
              {option.key}
            </Typography>
          </ColumnFlexBox>
        </li>
      )}
      renderInput={(params) => <TextField {...params} label={label} />}
    />
  );
};

/**
 * One declared setting, drawn by the control it declares.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.setting - The declaration, as `engine/settings.js` describes one.
 * @param {*} props.value - What the entry holds under it.
 * @param {Array} props.catalogue - The keys the section can show, for a setting naming one.
 * @param {Function} props.onChange - Receives the new value, undefined where it is cleared.
 *
 * @returns {React.JSX.Element}
 */
const SettingField = function SettingField({
  setting,
  value,
  catalogue = [],
  siblings = [],
  parameters = [],
  surface = 'list',
  onChange = () => {},
}) {
  const intl = useIntl();
  const translate = useTranslate();
  const theme = useTheme();

  const { name, label, control, choices, placeholder, min, max, step, help } =
    setting;
  const wording = translate(label);

  if (control === 'switch')
    return (
      <Tooltip arrow title={help ? translate(help) : ''}>
        <CenteredFlexBox gap="4px">
          <Switch
            id={`setting-${name}`}
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked || undefined)}
          />
          <Typography variant="body2">{wording}</Typography>
        </CenteredFlexBox>
      </Tooltip>
    );

  if (control === 'choice')
    return (
      <ColumnFlexBox gap="2px" sx={{ flex: 1, minWidth: '160px' }}>
        <Typography variant="caption" sx={{ opacity: '70%' }}>
          {wording}
        </Typography>
        <Select
          id={`setting-${name}`}
          size="small"
          displayEmpty
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || undefined)}
        >
          {(choices ?? []).map(({ value: held, label: choice }) => (
            <MenuItem key={choice} value={held}>
              {translate(choice)}
            </MenuItem>
          ))}
        </Select>
      </ColumnFlexBox>
    );

  if (control === 'icon')
    return (
      <ColumnFlexBox gap="2px" sx={{ flex: 1, minWidth: '160px' }}>
        <Typography variant="caption" sx={{ opacity: '70%' }}>
          {wording}
        </Typography>
        <IconSelect
          value={value ?? ''}
          onChange={(chosen) => onChange(chosen || undefined)}
          width="100%"
        />
      </ColumnFlexBox>
    );

  // A colour is the entry's own, or the one the value would otherwise be tinted with.
  if (control === 'colour')
    return (
      <ColumnFlexBox gap="2px" sx={{ flex: 'none' }}>
        <Typography variant="caption" sx={{ opacity: '70%' }}>
          {wording}
        </Typography>
        <Tooltip
          arrow
          title={value ? String(value) : intl.messages.DERIVED_COLOR}
        >
          <input
            type="color"
            aria-label={wording}
            value={
              String(value ?? '').startsWith('#')
                ? value
                : (String(value ?? '')
                    .split('.')
                    .reduce((node, part) => node?.[part], theme.palette) ??
                  theme.palette.primary.main)
            }
            onChange={(e) => onChange(e.target.value)}
            style={{
              width: '52px',
              height: '38px',
              padding: 0,
              border: value ? 'none' : '2px dashed',
              background: 'none',
              cursor: 'pointer',
            }}
          />
        </Tooltip>
      </ColumnFlexBox>
    );

  if (control === 'key')
    return (
      <Box sx={{ flex: 1, minWidth: '200px' }}>
        <KeyField
          label={wording}
          value={value ?? ''}
          catalogue={catalogue}
          onChange={(chosen) => onChange(chosen || undefined)}
        />
      </Box>
    );

  if (control === 'predicate')
    return (
      <PredicateField
        label={wording}
        value={value ?? null}
        surface={surface}
        catalogue={catalogue}
        siblings={siblings}
        parameters={parameters}
        onChange={onChange}
      />
    );

  if (control === 'dependsOn')
    return (
      <DependsOnField
        label={wording}
        value={value ?? null}
        siblings={siblings}
        onChange={onChange}
      />
    );

  // What the setting means is a tooltip rather than helper text: helper text is drawn under the
  // field, which makes it taller than the controls it shares a row with.
  return (
    <Tooltip arrow title={help ? translate(help) : ''}>
      <TextField
        id={`setting-${name}`}
        size="small"
        type={control === 'number' ? 'number' : 'text'}
        label={wording}
        placeholder={placeholder}
        value={value ?? ''}
        onChange={(e) =>
          onChange(
            e.target.value === ''
              ? undefined
              : control === 'number'
                ? Number(e.target.value)
                : e.target.value
          )
        }
        sx={{ flex: 1, minWidth: '160px' }}
        slotProps={{ htmlInput: { min, max, step } }}
      />
    </Tooltip>
  );
};

/**
 * A set of declared settings, wrapped across the width they are given.
 *
 * Nothing here knows what any of them are for: a type says what it reads and how it is chosen, and
 * this draws it. A type that comes to read something new becomes configurable by declaring it.
 *
 * @param {Object} props - Component props.
 * @param {Array} props.settings - The declarations to draw.
 * @param {Object} props.values - What the entry holds, by setting name.
 * @param {Array} props.catalogue - The keys the section can show, for a setting naming one.
 * @param {Array} props.siblings - The entries beside this one, for a setting naming one of them.
 * @param {Array} props.parameters - The runtime values the page publishes, by name.
 * @param {String} props.surface - The surface the entry belongs to.
 * @param {Function} props.onChange - Receives `{ [name]: value }` for the setting edited.
 *
 * @returns {React.JSX.Element}
 */
const SettingsFields = function SettingsFields({
  settings = [],
  values = {},
  catalogue = [],
  siblings = [],
  parameters = [],
  surface = 'list',
  onChange = () => {},
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        gap: '12px',
      }}
    >
      {settings.map((setting) => (
        <Box
          key={setting.name}
          sx={{
            flex: CONTROL_WIDTH[setting.control] ?? '1 1 200px',
            minWidth: 0,
          }}
        >
          <SettingField
            setting={setting}
            value={values?.[setting.name]}
            catalogue={catalogue}
            siblings={siblings}
            parameters={parameters}
            surface={surface}
            onChange={(value) => onChange({ [setting.name]: value })}
          />
        </Box>
      ))}
    </Box>
  );
};

export default SettingsFields;
